import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, ArrowRight } from 'lucide-react';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    title: 'Últimas',
    titleHighlight: 'Actualizaciones',
    readMore: 'Leer Más',
    news: [{
      date: '20 Abr, 2026',
      title: 'Gran Lanzamiento Oficial',
      excerpt: '¡Las puertas finalmente se abren! Únete a nosotros para la gran apertura oficial de MU Vanguard. Experimenta jugabilidad mejorada, nuevos eventos y recompensas épicas.',
      tag: 'Anuncio'
    }, {
      date: 'Próximamente',
      title: 'Evento Battle Royale',
      excerpt: 'Prepárate para la máxima prueba de supervivencia. Un nuevo modo de juego donde solo el más fuerte quedará en pie. ¡Mantente atento para más detalles!',
      tag: 'Evento'
    }, {
      date: 'Próximamente',
      title: 'Evento Rey del MU',
      excerpt: 'Demuestra tu valía en el torneo más grande del servidor. Compite contra los mejores jugadores por el mayor premio del año y el título de Rey del MU.',
      tag: 'Evento'
    }]
  },
  en: {
    title: 'Latest',
    titleHighlight: 'Updates',
    readMore: 'Read More',
    news: [{
      date: 'Apr 20, 2026',
      title: 'Official Grand Launch',
      excerpt: 'The gates are finally opening! Join us for the official grand opening of MU Vanguard. Experience enhanced gameplay, new events, and epic rewards.',
      tag: 'Announcement'
    }, {
      date: 'Coming Soon',
      title: 'Battle Royale Event',
      excerpt: 'Prepare for the ultimate survival test. A new game mode where only the strongest will remain standing. Stay tuned for more details!',
      tag: 'Event'
    }, {
      date: 'Coming Soon',
      title: 'King of MU Event',
      excerpt: 'Prove your worth in the biggest tournament of the server. Compete against the best players for the biggest prize of the year and the title of King of MU.',
      tag: 'Event'
    }]
  },
  pt: {
    title: 'Últimas',
    titleHighlight: 'Atualizações',
    readMore: 'Ler Mais',
    news: [{
      date: '20 Abr, 2026',
      title: 'Grande Lançamento Oficial',
      excerpt: 'Os portões finalmente se abrem! Junte-se a nós para a grande abertura oficial do MU Vanguard. Experimente jogabilidade aprimorada, novos eventos e recompensas épicas.',
      tag: 'Anúncio'
    }, {
      date: 'Em Breve',
      title: 'Evento Battle Royale',
      excerpt: 'Prepare-se para o teste final de sobrevivência. Um novo modo de jogo onde apenas o mais forte permanecerá de pé. Fique atento para mais detalhes!',
      tag: 'Evento'
    }, {
      date: 'Em Breve',
      title: 'Evento Rei do MU',
      excerpt: 'Prove seu valor no maior torneio do servidor. Compita contra os melhores jogadores pelo maior prêmio do ano e o título de Rei do MU.',
      tag: 'Evento'
    }]
  }
};
const newsImages = ['https://cdn.magicpatterns.com/uploads/6hvWMPRcrc7W6dReYQu3EU/image0.png', 'https://cdn.magicpatterns.com/uploads/uxePzFMtKP9s7Ms6pFk9S8/7A4E9F2E-D452-4C65-8C17-8C32DCB2FBE4.png', 'https://cdn.magicpatterns.com/uploads/1eiNY5tQRvAFZ3U4eMNAQE/image.png'];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function News() {
  const [language, setLanguage] = useState<Language>('es');
  const t = translations[language];
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  return <section id="news" className="py-16 md:py-24 relative z-10 bg-darkerBlue" data-id="element-404">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-405">
        <div className="text-center mb-16" data-id="element-406">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-id="element-407">
            {t.title}{' '}
            <span className="text-gradient-gold" data-id="element-408">{t.titleHighlight}</span>
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          scale: 0
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="w-24 h-1 bg-gold mx-auto rounded-full" data-id="element-409" />
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="news-list" data-field="content.news" data-id="element-410">
          {t.news.map((item, index) => <motion.article key={index} variants={itemVariants} data-testid="news-card" data-field={`news.${index}`} className="glass-panel rounded-2xl overflow-hidden group flex flex-col h-full border border-white/5 hover:border-gold/30 transition-colors duration-300" data-id="element-411">
              <div className="relative h-48 overflow-hidden" data-id="element-412">
                <div className="absolute inset-0 bg-darkerBlue/40 group-hover:bg-transparent transition-colors duration-500 z-10" data-id="element-413" />
                <img src={newsImages[index]} alt={item.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" data-id="element-414" />
                <div className="absolute top-4 left-4 z-20" data-id="element-415">
                  <span className="px-3 py-1 bg-darkBlue/80 backdrop-blur-md border border-white/10 rounded-full text-xs font-bold text-gold uppercase tracking-wider" data-id="element-416">
                    {item.tag}
                  </span>
                </div>
              </div>

              <div className="p-6 flex flex-col flex-1" data-id="element-417">
                <div className="flex items-center gap-2 text-sm text-gray-400 mb-3" data-id="element-418">
                  <CalendarDays className="w-4 h-4 text-glowBlue" data-id="element-419" />
                  {item.date}
                </div>

                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-gold transition-colors line-clamp-2" data-id="element-420">
                  {item.title}
                </h3>

                <p className="text-gray-400 mb-6 line-clamp-3 flex-1" data-id="element-421">
                  {item.excerpt}
                </p>

                <a href={`#news-${index}`} className="inline-flex items-center gap-2 text-glowBlue hover:text-white font-semibold transition-colors mt-auto group/link" data-id="element-422">
                  {t.readMore}
                  <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" data-id="element-423" />
                </a>
              </div>
            </motion.article>)}
        </motion.div>
      </div>
    </section>;
}